import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { eur } from "@/lib/format";
import { Save } from "lucide-react";

const BUCKETS = [0, 25, 50, 75, 100];

/**
 * Dialog zum Anlegen / Überschreiben eines **manuellen Baseline-Snapshots**
 * (z. B. 2023, wenn keine Bewegungsdaten in der App vorliegen).
 *
 *   - Admin gibt pro Erlösgruppe × WB-Bucket den **Brutto-Lagerwert** ein.
 *   - WB und Bilanzwert werden live aus dem Bucket-Prozentsatz berechnet
 *     (analog zur Backend-Logik).
 *   - Beim Speichern wird der Snapshot upserted und nach Erfolg neu geladen.
 *
 * Die Erlösgruppen-Liste wird aus dem zuletzt vorhandenen Snapshot des
 * Mandanten übernommen, kann aber ergänzt / umbenannt werden.
 */
export default function ManualSnapshotDialog({
    open,
    onOpenChange,
    initialZeitraum = "2023",
    initialZeitraumTyp = "jahr",
    erloesgruppenVorgaben = [],
    onSaved,
}) {
    const { activeTenant } = useTenant();
    const [zeitraum, setZeitraum] = useState(initialZeitraum);
    const [zeitraumTyp] = useState(initialZeitraumTyp);
    const [notiz, setNotiz] = useState("");
    const [erloesgruppen, setErloesgruppen] = useState([]);
    const [werte, setWerte] = useState({}); // { "<eg>::<bucket>": "1234.56" }
    const [saving, setSaving] = useState(false);
    const [existing, setExisting] = useState(null);

    // Initial Erlösgruppen befüllen, sobald sich Dialog öffnet
    useEffect(() => {
        if (!open) return;
        setZeitraum(initialZeitraum);
        setNotiz("");
        setWerte({});
        setExisting(null);
        // Standard-Erlösgruppen: Vorgaben (vom Parent), Fallback = AB + KJ.
        const defaults = erloesgruppenVorgaben && erloesgruppenVorgaben.length
            ? erloesgruppenVorgaben
            : ["3: Annette Betz", "4: Ueberreuter Kinder- und Jugendbuch"];
        setErloesgruppen(defaults);
        // Prüfen ob bereits ein Baseline-Snapshot existiert → Werte vor-laden
        if (activeTenant?.tenant_id) {
            (async () => {
                try {
                    const r = await api.get("/snapshots", {
                        params: { tenant_id: activeTenant.tenant_id },
                    });
                    const ex = r.data.find(
                        (s) => s.zeitraum === initialZeitraum && s.zeitraum_typ === initialZeitraumTyp,
                    );
                    if (ex) {
                        setExisting(ex);
                        const pre = {};
                        const egSet = new Set();
                        for (const row of ex.aufschluesselung || []) {
                            pre[`${row.erloesgruppe}::${row.wb_bucket}`] = String(row.lagerwert || "");
                            egSet.add(row.erloesgruppe);
                        }
                        setWerte(pre);
                        if (egSet.size) setErloesgruppen(Array.from(egSet));
                        setNotiz(ex.notiz || "");
                    }
                } catch {
                    // ignorieren — falls Snapshots nicht laden, leerer Modus
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, activeTenant?.tenant_id, initialZeitraum, initialZeitraumTyp]);

    const parseNum = (s) => {
        if (s === null || s === undefined || s === "") return 0;
        const cleaned = String(s).replace(/\./g, "").replace(",", ".").replace(/\s+/g, "");
        const n = parseFloat(cleaned);
        return isFinite(n) ? n : 0;
    };

    // Live-Berechnung pro Zelle
    const cellCompute = (egVal, bucket) => {
        const lager = parseNum(werte[`${egVal}::${bucket}`]);
        const wb = lager * (bucket / 100);
        const bilanz = lager - wb;
        return { lager, wb, bilanz };
    };

    // Live-Summen
    const summen = useMemo(() => {
        let lg = 0, w = 0, b = 0;
        for (const eg of erloesgruppen) {
            for (const bk of BUCKETS) {
                const c = cellCompute(eg, bk);
                lg += c.lager;
                w += c.wb;
                b += c.bilanz;
            }
        }
        return { lager: lg, wb: w, bilanz: b };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [werte, erloesgruppen]);

    const updateZelle = (eg, bucket, v) => {
        setWerte((w) => ({ ...w, [`${eg}::${bucket}`]: v }));
    };

    const addErloesgruppe = () => {
        const name = window.prompt('Name der neuen Erlösgruppe (z. B. "5: Sachbuch")?');
        if (!name) return;
        if (erloesgruppen.includes(name.trim())) {
            toast.error("Erlösgruppe existiert bereits");
            return;
        }
        setErloesgruppen([...erloesgruppen, name.trim()]);
    };

    const removeErloesgruppe = (eg) => {
        if (!window.confirm(`Erlösgruppe „${eg}" aus diesem Baseline-Snapshot entfernen?`)) return;
        setErloesgruppen(erloesgruppen.filter((x) => x !== eg));
        // Werte dieser Gruppe löschen
        const next = { ...werte };
        for (const bk of BUCKETS) delete next[`${eg}::${bk}`];
        setWerte(next);
    };

    const speichern = async () => {
        if (!activeTenant?.tenant_id) return;
        const eintraege = [];
        for (const eg of erloesgruppen) {
            for (const bk of BUCKETS) {
                const lager = parseNum(werte[`${eg}::${bk}`]);
                // Auch 0-Einträge mitsenden, damit beim Re-Speichern Alt-Werte überschrieben werden.
                eintraege.push({ erloesgruppe: eg, wb_bucket: bk, lagerwert: lager });
            }
        }
        setSaving(true);
        try {
            const r = await api.post("/snapshots/manual", {
                tenant_id: activeTenant.tenant_id,
                zeitraum,
                zeitraum_typ: zeitraumTyp,
                notiz,
                eintraege,
            });
            toast.success(`Baseline ${zeitraum} gespeichert · Bilanz ${eur(r.data.summen.bilanzwert)}`);
            onSaved?.(r.data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="manual-snapshot-dialog">
                <DialogHeader>
                    <DialogTitle>Baseline-Snapshot manuell anlegen</DialogTitle>
                    <DialogDescription>
                        Für Jahre ohne Bewegungsdaten (z. B. 2023) kannst Du hier den Bilanz-Snapshot
                        direkt eingeben — pro <strong>Erlösgruppe × Wertberichtigungs-Bucket</strong>{" "}
                        ist nur der <strong>Brutto-Lagerwert</strong> nötig. <strong>WB</strong> und{" "}
                        <strong>Bilanzwert</strong> werden automatisch aus dem Bucket-Prozentsatz berechnet.
                        {existing && (
                            <span className="block mt-2 text-amber-300">
                                Achtung: Es existiert bereits ein Snapshot für {zeitraum} — Werte wurden vorgeladen
                                und werden beim Speichern überschrieben.
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div>
                        <Label className="text-xs">Zeitraum (Jahr)</Label>
                        <Input
                            value={zeitraum}
                            onChange={(e) => setZeitraum(e.target.value)}
                            className="mt-1 tabular"
                            data-testid="manual-snap-year-input"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label className="text-xs">Notiz (optional)</Label>
                        <Input
                            value={notiz}
                            onChange={(e) => setNotiz(e.target.value)}
                            placeholder="z. B. Baseline aus Excel UeV-Inventur 2023"
                            className="mt-1"
                            data-testid="manual-snap-notiz-input"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm tabular">
                        <thead className="bg-surface2">
                            <tr>
                                <th className="text-left p-2 text-xs uppercase tracking-wider text-muted-foreground">
                                    Erlösgruppe
                                </th>
                                {BUCKETS.map((b) => (
                                    <th
                                        key={b}
                                        className="text-right p-2 text-xs uppercase tracking-wider text-muted-foreground"
                                        title={`WB-Bucket ${b}%: WB = Lager × ${b}%`}
                                    >
                                        Lagerwert {b}%
                                    </th>
                                ))}
                                <th className="text-right p-2 text-xs uppercase tracking-wider text-muted-foreground">
                                    Σ Bilanz
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {erloesgruppen.map((eg) => {
                                let zsBilanz = 0;
                                const cells = BUCKETS.map((bk) => {
                                    const c = cellCompute(eg, bk);
                                    zsBilanz += c.bilanz;
                                    return { bk, c };
                                });
                                return (
                                    <tr key={eg} className="border-t border-border">
                                        <td className="p-2 text-sm font-medium">{eg}</td>
                                        {cells.map(({ bk, c }) => (
                                            <td key={bk} className="p-1">
                                                <Input
                                                    value={werte[`${eg}::${bk}`] ?? ""}
                                                    onChange={(e) => updateZelle(eg, bk, e.target.value)}
                                                    placeholder="0,00"
                                                    className="text-right tabular h-8 text-xs px-2"
                                                    data-testid={`manual-input-${eg}-${bk}`}
                                                />
                                                <div className="text-[9px] text-muted-foreground text-right pr-1 tabular">
                                                    WB {eur(c.wb)} · Bil {eur(c.bilanz)}
                                                </div>
                                            </td>
                                        ))}
                                        <td className="text-right p-2 font-semibold text-emerald-400 tabular">
                                            {eur(zsBilanz)}
                                        </td>
                                        <td className="p-1 text-right">
                                            <button
                                                type="button"
                                                onClick={() => removeErloesgruppe(eg)}
                                                className="text-xs text-red-400 hover:underline px-2"
                                                data-testid={`manual-remove-${eg}`}
                                            >
                                                Entfernen
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-surface2">
                            <tr>
                                <td className="p-2 text-xs uppercase tracking-wider text-muted-foreground">
                                    Σ Gesamt
                                </td>
                                <td colSpan={BUCKETS.length} className="p-2 text-right text-xs text-muted-foreground tabular">
                                    Lager {eur(summen.lager)} · WB {eur(summen.wb)}
                                </td>
                                <td className="p-2 text-right font-semibold text-emerald-400 tabular">
                                    {eur(summen.bilanz)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="flex justify-between items-center mt-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={addErloesgruppe}
                        data-testid="manual-add-eg"
                    >
                        + Erlösgruppe hinzufügen
                    </Button>
                    <div className="text-xs text-muted-foreground">
                        Tipp: Leere Buckets = 0 €.
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Abbrechen
                    </Button>
                    <Button
                        onClick={speichern}
                        disabled={saving || !zeitraum}
                        className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                        data-testid="manual-save-btn"
                    >
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? "Speichere …" : `Baseline ${zeitraum} speichern`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
